import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useResolveMismatch } from '../../hooks/useSparkActivities';
import { useDomains } from '../../hooks/useDomains';
import PineSelect from './PineSelect';
import type { ResolveMismatchPayload } from '../../types/spark-activity';

interface MismatchData {
  domains?: Record<string, string>;
  skills?: Record<string, string[]>;
  preferences?: string[];
  packs?: string[];
  playStyles?: string[];
  duration?: string | null;
}

interface MismatchResolutionPanelProps {
  activityId: number;
  mismatch: Record<string, unknown>;
}

const DURATION_OPTIONS = [
  { label: '5–10 min', value: '5-10', duration: 5, durationMax: 10 },
  { label: '10–15 min', value: '10-15', duration: 10, durationMax: 15 },
  { label: '15–20 min', value: '15-20', duration: 15, durationMax: 20 },
  { label: '20–30 min', value: '20-30', duration: 20, durationMax: 30 },
];

const STAGE_LABELS: Record<string, string> = {
  L1: 'L1 — Gestures',
  L2: 'L2 — Single words',
  L3: 'L3 — Two words',
  L4: 'L4 — Short sentences',
  L5: 'L5 — Conversations',
};

function MismatchChip({ value }: { value: string }) {
  return (
    <span className="px-3 py-1.5 rounded-full text-[12.5px] font-semibold border bg-amber-100 text-amber-700 border-amber-300">
      {value}
    </span>
  );
}

export default function MismatchResolutionPanel({
  activityId,
  mismatch,
}: MismatchResolutionPanelProps) {
  const data = mismatch as MismatchData;

  const { data: domains = [] } = useDomains();

  const resolveMutation = useResolveMismatch();

  const [resolvedDomains, setResolvedDomains] = useState<Record<string, number>>({});
  const [resolvedDuration, setResolvedDuration] = useState<string | null>(null);

  const mismatchedDomainLevels = Object.keys(data.domains ?? {});
  const mismatchedSkillLevels = Object.keys(data.skills ?? {});
  const hasMismatchedPreferences = (data.preferences ?? []).length > 0;
  const hasMismatchedPacks = (data.packs ?? []).length > 0;
  const hasMismatchedPlayStyles = (data.playStyles ?? []).length > 0;
  const hasMismatchedDuration = !!data.duration;

  const allDomainsResolved =
    mismatchedDomainLevels.length === 0 || mismatchedDomainLevels.every((l) => resolvedDomains[l]);
  const durationResolved = !hasMismatchedDuration || resolvedDuration !== null;
  const canResolve = allDomainsResolved && durationResolved;

  const handleResolve = () => {
    const payload: ResolveMismatchPayload = {};

    if (Object.keys(resolvedDomains).length > 0) payload.domains = resolvedDomains;
    if (resolvedDuration !== null) {
      const opt = DURATION_OPTIONS.find((o) => o.value === resolvedDuration);
      if (opt) {
        payload.duration = opt.duration;
        payload.durationMax = opt.durationMax;
      }
    }

    resolveMutation.mutate({ activityId, data: payload });
  };

  const domainOptions = [
    { value: '', label: 'Select domain…' },
    ...domains.map((d) => ({ value: d.id, label: d.title })),
  ];

  return (
    <div className="border-[1.5px] border-amber-300 bg-amber-50 rounded-[12px] p-[20px] mt-[26px]">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <h3 className="text-[13px] font-bold text-amber-800 uppercase tracking-[0.08em]">
          Unresolved Mismatches
        </h3>
      </div>
      <p className="text-[12px] text-amber-700 mb-4">
        The following fields from the CSV import could not be matched automatically. Select the correct values below and click Resolve.
      </p>

      {/* Duration mismatch */}
      {hasMismatchedDuration && (
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 mb-2">
            Duration
          </p>
          <div className="flex flex-wrap gap-[6px] mb-2">
            <MismatchChip value={data.duration!} />
          </div>
          <PineSelect
            options={DURATION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={resolvedDuration ?? ''}
            onChange={(v) => setResolvedDuration(String(v))}
            placeholder="Select duration…"
          />
        </div>
      )}

      {/* Domain mismatches per level */}
      {mismatchedDomainLevels.length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 mb-2">
            Domains
          </p>
          <div className="flex flex-col gap-2">
            {mismatchedDomainLevels.map((level) => (
              <div key={level} className="flex items-center gap-3">
                <div className="w-[130px] flex-shrink-0">
                  <span className="text-[11px] font-semibold text-amber-700 block mb-1">
                    {STAGE_LABELS[level] ?? level}
                  </span>
                  <MismatchChip value={data.domains![level]} />
                </div>
                <div className="flex-1">
                  <PineSelect
                    options={domainOptions}
                    value={resolvedDomains[level] ?? ''}
                    onChange={(v) =>
                      setResolvedDomains((prev) => ({ ...prev, [level]: Number(v) }))
                    }
                    placeholder="Select domain…"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skill mismatches per level */}
      {mismatchedSkillLevels.length > 0 && (
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 mb-2">
            Skills
          </p>
          {mismatchedSkillLevels.map((level) => (
            <div key={level} className="mb-3">
              <p className="text-[11px] font-semibold text-amber-700 mb-1">
                {STAGE_LABELS[level] ?? level}
              </p>
              <div className="flex flex-wrap gap-[6px]">
                {(data.skills![level] ?? []).map((v) => (
                  <MismatchChip key={v} value={v} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preference mismatches */}
      {hasMismatchedPreferences && (
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 mb-2">
            Child Preferences
          </p>
          <div className="flex flex-wrap gap-[6px]">
            {(data.preferences ?? []).map((v) => (
              <MismatchChip key={v} value={v} />
            ))}
          </div>
        </div>
      )}

      {/* Pack mismatches */}
      {hasMismatchedPacks && (
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 mb-2">
            Physical Deck (Packs)
          </p>
          <div className="flex flex-wrap gap-[6px]">
            {(data.packs ?? []).map((v) => (
              <MismatchChip key={v} value={v} />
            ))}
          </div>
        </div>
      )}

      {/* Play style mismatches */}
      {hasMismatchedPlayStyles && (
        <div className="mb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 mb-2">
            Play Style
          </p>
          <div className="flex flex-wrap gap-[6px]">
            {(data.playStyles ?? []).map((v) => (
              <MismatchChip key={v} value={v} />
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleResolve}
        disabled={!canResolve || resolveMutation.isPending}
        className="mt-2 px-4 py-2 rounded-pine-input text-[13px] font-semibold transition-colors
          bg-amber-500 text-white hover:bg-amber-600
          disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {resolveMutation.isPending ? 'Resolving…' : 'Resolve'}
      </button>
    </div>
  );
}
